// Top-of-app account banners.
//
// Two stacked banners that may surface on any authenticated page:
//
//   1. AdminMessageBanner — shown when the current user has an admin
//      special message attached. Dismiss button hits the ack endpoint
//      which clears the message server-side. Re-fetches the user so
//      the banner disappears immediately.
//
//   2. VerificationBanner — shown when the user is not yet
//      email-verified. Offers a "Resend verification email" CTA.
//      If the configured deadline has elapsed and the page is the
//      restricted app-shell (handled by VerificationGate below), the
//      banner explains the access limitation.
//
// Why two banners not one stack: admin messages are operator-driven
// and rare; verification is structural and common. Keeping them as
// independent components keeps the styling/copy distinct and avoids
// a "one banner suppresses the other" race.
//
// Auth state is read from useAuthContext. Both banners no-op for
// guest/unauthenticated visitors.

import { useEffect, useState } from 'react';
import { profileCompletion } from '../../config/profileOptions.js';
import { useAuthContext } from '../../context/AuthContext.jsx';
import { identity } from '../../api/identity.js';
import { isReviewer } from '@moksy/common/src/permissions/reviewerSeparation.js';
import { Icon } from '../elements/icon.element.jsx';

import { Text } from '../elements/text.element.jsx';
import { Button } from '../elements/Button.jsx';
import { errorText } from '../../utils/errorText';
const BRAND = 'var(--moksy-brand)';

export const AdminMessageBanner = () => {
  const { user, refreshAuth } = useAuthContext();
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const message = user?.adminSpecialMessage;
  if (!message || dismissed) return null;

  const ack = async () => {
    setBusy(true);
    try {
      await identity.acknowledgeAdminMessage();
      setDismissed(true);
      await refreshAuth?.();
    } catch (_) {
      // Don't surface — the banner is informational, ack-failure
      // shouldn't block the user. They can dismiss locally for now.
      setDismissed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-b border-indigo-200 bg-indigo-50">
      <div className="mx-auto flex max-w-7xl items-start justify-between gap-3 px-4 py-2 text-sm text-indigo-900">
        <div className="flex items-start gap-2">
          <span aria-hidden="true">📣</span>
          <div>
            <div className="font-semibold">A message from the Moksy team</div>
            <div className="text-xs text-indigo-800/90 mt-0.5 whitespace-pre-line">{message}</div>
          </div>
        </div>
        <Button variant="outline" size="sm" className="shrink-0"
          disabled={busy}
          onClick={ack}>
          {busy ? 'Acknowledging…' : 'Got it'}
        </Button>
      </div>
    </div>
  );
};

// Friendly verification reminder while the user is within the grace
// period. Hides once gated (the full-screen VerificationGate takes over).
export const VerificationBanner = () => {
  const { user } = useAuthContext();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  // Guests and verified users see nothing. Owner-group accounts
  // (super_admin/admin/etc.) are also excluded — operator accounts are
  // bootstrapped without going through the email flow and the banner
  // would just be noise.
  if (!user) return null;
  if (user.isVerified) return null;
  if (String(user.group || '').toLowerCase() === 'owner') return null;
  // Deadline-elapsed accounts are handled by VerificationGate, which
  // replaces the page content entirely. Suppress the banner there to
  // avoid stacking two messages about the same problem.
  if (user?.verification?.gated) return null;

  const hoursRemaining = Number(user?.verification?.hoursRemaining);
  const subline = Number.isFinite(hoursRemaining) && hoursRemaining > 0
    ? `Verify within ${Math.ceil(hoursRemaining)} hours to keep full access.`
    : 'Some actions stay disabled until your email is verified.';

  const resend = async () => {
    setSending(true);
    setError(null);
    try {
      await identity.requestEmailVerification({ email: user.email });
      setSent(true);
    } catch (e) {
      setError(errorText(e, 'Could not send verification email.'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="border-b border-amber-200 bg-amber-50">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-2 text-sm text-amber-900">
        <div className="flex items-center gap-2">
          <span aria-hidden="true">📬</span>
          <div>
            <span className="font-semibold">Verify your email</span>
            <span className="ml-2 text-xs text-amber-800/90">
              We sent a confirmation link to {user.email || 'your inbox'}. {subline}
            </span>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {sent ? (
            <span className="text-[11px] font-semibold text-emerald-800">Email sent ✓</span>
          ) : null}
          {error ? (
            <span className="text-[11px] font-semibold text-rose-700">{error}</span>
          ) : null}
          <Button variant="outline" size="sm"
            disabled={sending || sent}
            onClick={resend}>
            {sending ? 'Sending…' : sent ? 'Sent' : 'Resend email'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Full-screen block for deadline-elapsed unverified accounts. The user
// stays logged in (so they can resend verification + log out + retry
// from a real link) but cannot reach app pages until they confirm
// their email. Lets owner-group through unconditionally.
export const VerificationGate = ({ children }) => {
  const { user } = useAuthContext();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  if (!user) return children;
  if (user.isVerified) return children;
  if (String(user.group || '').toLowerCase() === 'owner') return children;
  if (!user?.verification?.gated) return children;

  const resend = async () => {
    setSending(true);
    setError(null);
    try {
      await identity.requestEmailVerification({ email: user.email });
      setSent(true);
    } catch (e) {
      setError(errorText(e, 'Could not send verification email.'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-6 py-16">
      <div className="max-w-md w-full rounded-2xl border border-amber-200 bg-amber-50 p-6 shadow-sm">
        <div className="flex items-start gap-3">
          <span aria-hidden="true" className="text-2xl">⏳</span>
          <div className="flex-1">
            <Text as="h2" variant="title">Email verification required</Text>
            <p className="mt-2 text-sm text-amber-900/90 leading-relaxed">
              The grace period for verifying <strong>{user.email}</strong> has ended.
              We've kept your account active, but app access stays paused
              until your email is confirmed.
            </p>
            <p className="mt-2 text-xs text-amber-800/80">
              Open the confirmation link we emailed you, or request a fresh one below.
            </p>
            <div className="mt-4 flex items-center gap-3">
              <Button size="sm"
                disabled={sending || sent}
                onClick={resend}>
                {sending ? 'Sending…' : sent ? 'Sent ✓' : 'Send a fresh link'}
              </Button>
              {error ? (
                <span className="text-[11px] font-semibold text-rose-700">{error}</span>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * 📝 Profile completion.
 *
 * A BANNER, not a gate — deliberately weaker than VerificationGate above.
 *
 * Email verification blocks the app because an unverified address means we
 * cannot reach the person at all: every reset, receipt and notification is
 * undeliverable, so letting them build work on that account creates a debt
 * that gets paid later and painfully. A missing surname does not have that
 * property. Nothing downstream is broken by it; it is merely worse. Locking
 * somebody out of work they paid for over a name field would be a punishment
 * out of proportion to the problem, and the reliable result is a profile
 * filled with "x" to get past the wall.
 *
 * So: persistent, specific, one click away from being fixed — and it names
 * the missing fields, because "your profile is incomplete" tells somebody
 * they are being judged without telling them what to do about it.
 */
export const ProfileCompletionBanner = () => {
  const { user, activeRole } = useAuthContext();
  const [about, setAbout] = useState(null);
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem('moksy_profile_prompt_dismissed') === '1'
  );

  useEffect(() => {
    if (!user) return undefined;
    // Operators are exempt: a staff account is not addressed on an invoice or
    // shown to a reviewer, so the fields this asks for have no consumer.
    if (String(user.group || '').toLowerCase() === 'owner') return undefined;
    let mounted = true;
    // `me()` is what the profile page itself reads — one endpoint, one shape.
    // A dedicated getAbout() would be a second reader of the same data, free
    // to disagree with the page it is prompting people to go and fill in.
    identity.me()
      .then((data) => { if (mounted) setAbout(data?.about || {}); })
      // Silent: a failed background read must not put an error in front of
      // somebody who did not ask for anything.
      .catch(() => { if (mounted) setAbout(null); });
    return () => { mounted = false; };
  }, [user?.id, user?.group]);

  if (!user || !about || dismissed) return null;
  const { complete, missing } = profileCompletion(about);
  if (complete) return null;
  // The ACTIVE persona, not the roles they hold. Keying this on `isReviewer`
  // alone meant a reviewer wearing the customer hat was still told they were
  // missing from the reviewer directory — reviewer copy on a customer screen,
  // which is the exact mistake this banner was fixed to stop making.
  const wearingReviewerHat = ['reviewer', 'lead_reviewer']
    .includes(String(activeRole || '').toLowerCase());
  const reviewerAudience = isReviewer(user) && wearingReviewerHat;

  return (
    <div className="border-b border-sky-200 bg-sky-50 px-4 py-3 md:px-8">
      <div className="mx-auto flex max-w-[1600px] flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        {/* 🎖️ A reviewer pays a different price for an unfinished profile.
            This told everyone the same thing — that a name is "how you are
            addressed on reviews, messages and invoices" — while for a reviewer
            the actual cost is that `mayListPublicly` keeps them OUT of the
            directory customers choose from. The profile page says so plainly;
            the banner people actually see on every page did not, so a reviewer
            wondering why nobody picks them had to go looking. */}
        {/* The banner led with a bare sentence, so on a page of icon-led
            cards it read as an unstyled paragraph that had escaped the
            layout rather than as a deliberate prompt. */}
        <p className="flex items-start gap-2 text-sm text-sky-900">
          <Icon name="user" className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
          <span className="font-semibold">Finish your profile.</span>{' '}
          We still need your {missing.map((f) => f.label.toLowerCase()).join(' and ')} —
          {reviewerAudience
            ? ' until it is done you are not listed in the directory customers pick reviewers from.'
            : ' it is how you are addressed on reviews, messages and invoices.'}
          </span>
        </p>
        <div className="flex shrink-0 items-center gap-2">
          <a
            href="/profile?tab=about"
            className="inline-flex items-center gap-1 rounded-xl bg-sky-900 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-white hover:opacity-90"
          >
            <Icon name="plus" className="h-3.5 w-3.5" />
            Add it
          </a>
          <Button variant="ghost" size="sm"
            // Dismissal is per-SESSION, not permanent. Permanent means it is
            // seen once and the profile stays empty forever; per-tab means it
            // stops nagging today and asks again tomorrow.
            onClick={() => {
              sessionStorage.setItem('moksy_profile_prompt_dismissed', '1');
              setDismissed(true);
            }}>
            Later
          </Button>
        </div>
      </div>
    </div>
  );
};

export const AccountBanners = () => (
  <>
    <AdminMessageBanner />
    <VerificationBanner />
    <ProfileCompletionBanner />
  </>
);

export default AccountBanners;
